import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import _ from 'lodash';
import cx from 'classnames';
import Lottie from 'react-lottie';
import { Frame } from "scenejs";

import { EditorContext } from '../../../index';

import { createWidget, toggleEditMode, updateWidgetProperty } from 'actions/editor';
import { Property } from '../../../components/properties/';
import { SCROLL_EFFECTS } from 'constants/scrollEffects';
import { CONTENT_FILTER, CONTENT_FILTER_CONTAINER, CONTENT_SEARCH } from 'constants/widgetTypes';
import { SearchWidgetRequset } from 'constants/contentGrid';
import { useChildrenWidgetSelected, useWidgetSelected, useWidgetSelector } from 'components/Editor/hooks/useEditorSession';

import { LiveElementCreationProperty } from '../../../components/liveElements';
import { Background } from 'components/Editor/components/properties/background';
import { BackgroundView } from '../../background';

import styles from './filters.module.scss';
import { getMediaInfo } from 'actions/pageHealth';

const IMAGE_CDN_ENDPOINT = process.env.REACT_APP_IMAGE_CDN;

const FilterWidget = React.memo(function FilterWidget({ className, style, widgetProps, widgetRef, children }) {
  const dispatch = useDispatch();
  const ref = widgetRef;

  const [filtersWrapperStyle, setFiltersWrapperStyle] = useState({})

  const editorContext = React.useContext(EditorContext);
  const editor = editorContext.editorSession.id;

  const id = _.get(widgetProps, 'uid');
  const type = _.get(widgetProps, 'type');

  const breakpoint = useSelector(state => _.get(state, `${editor}.present.breakpoint`));
  const parent = _.get(widgetProps, 'parent');
  const background = _.get(widgetProps, 'properties.desktop.background');
  const properties = _.get(widgetProps, `properties.${breakpoint}`, {});
  const visible = _.get(widgetProps, `properties.${breakpoint}.visible`);
  const filterSectionHeight = parseInt(_.get(properties, 'layout.height'));
  const spacing = parseInt(_.get(properties, 'spacing'));
  const parentWidgetProps = useWidgetSelector(editor, parent)
  const parentSelected = useWidgetSelected(editor, parent);
  const childrenSelection = useChildrenWidgetSelected(editor, id);
  const showFilters = _.get(parentWidgetProps, 'properties.desktop.filters', false);
  const showSearch = _.get(parentWidgetProps, 'properties.desktop.search', false);
  const filters = children.some(child => 
    child?.props?.widgetProps?.type === "cleverstory.v2.widgets.content.filter.container" &&
    child?.props?.widgetProps?.content?.length > 0
  ); 
  const alignment = getContainerAlignment(widgetProps, breakpoint, filters);
  const buttonAlignmnet = getDropDownAlignment(widgetProps, breakpoint);

  const selectedId = useSelector(state => {
    return _.get(state, `${editor}.present.editMode`);
  });
  const isAnyChildSelected = children.some(child => _.get(child, 'props.widgetProps.uid') === selectedId);

  useEffect(() => {

    var sectionWidgetStyle = {
      ...style,
      ...getStyles(widgetProps, breakpoint),
      transform: 'none'
    }
    setFiltersWrapperStyle(sectionWidgetStyle)
  }, [breakpoint, widgetProps])

  useEffect(() => {
    const searchContainer = children.some(child => _.get(child, 'props.widgetProps.type') === CONTENT_SEARCH);
    if (!searchContainer && showSearch) {
      dispatch(createWidget(editor, [SearchWidgetRequset], id, false));
    }
  }, [showSearch])

  const handlePropertyChange = (value) => {
    dispatch(updateWidgetProperty(editor, id, breakpoint, value));
  }

  useEffect(() => {
    if (showFilters && showSearch) {
      handleSpaceBetween();
    }
  }, [filterSectionHeight])

  const maxVerticalMargin = useSelector(state => {
    const widgets = _.get(state, `${editor}.present.widgets`) || [];
    let filterContainer = widgets.find(item => item.parent === id && item.type === CONTENT_FILTER_CONTAINER);
    let searchContainer = widgets.find(item => item.parent === id && item.type === CONTENT_SEARCH);
    const filterElement = filterContainer ? document.getElementById(filterContainer.uid) : null;
    const searchElement = searchContainer ? document.getElementById(searchContainer.uid) : null;

    if (filterElement && searchElement && showFilters && showSearch) {
      const filterHeight = filterElement.clientHeight || 0;
      const searchHeight = searchElement.clientHeight || 0;
      return filterSectionHeight - parseInt(filterHeight + searchHeight);
    }

    return filterSectionHeight;
  });

  const handleSpaceBetween = () => {
    if (maxVerticalMargin < 0) {
      return;
    }

    if (spacing > maxVerticalMargin) {
      const spaceRequest = {
        ...properties,
        spacing: _.max([maxVerticalMargin, 0])
      }
      handlePropertyChange(spaceRequest)
    }
  }

  useEffect(() => {
    if (!showFilters || !showSearch) {
      var horizontalAlignmentRequest = {
        ...properties,
        horizontalColumn: false
      }
      handlePropertyChange(horizontalAlignmentRequest)
    }
  }, [showFilters, showSearch])

  return (
    <div
      id={id}
      ref={ref}
      className={cx(className, styles.filterSection, 'widget', {
        [styles.parentSelected]: parentSelected,
        [styles.partialSelection]: childrenSelection.selected,
      })}
      style={filtersWrapperStyle}
      data-widget-id={id}
      data-widget-type={type}
      data-selectable={!childrenSelection.selected}
    >
      <BackgroundView background={background} />
      <div style={alignment} className={cx(styles.childContainer)}>
        {React.Children.map(children, (child, index) => {
          const childType = _.get(child, 'props.widgetProps.type');
          if (childType == CONTENT_FILTER_CONTAINER && showFilters && visible) {
            return React.cloneElement(child, {
              key: index,
              parentRef: ref,
              parentProps: widgetProps,
              alignment: buttonAlignmnet
            });
          }

          if (childType == CONTENT_SEARCH && showSearch && visible) {
            return React.cloneElement(child, {
              key: index,
              parentRef: ref,
              parentProps: widgetProps,
              alignment: buttonAlignmnet
            });
          }
        })}
      </div>
    </div>
  )
})

const FilterWidgetDesktopAction = (props) => {
  const dispatch = useDispatch();
  const [view, setView] = useState(null);

  const editorContext = React.useContext(EditorContext);
  const editorSession = editorContext.editorSession.id;

  const id = _.get(props, 'uid');
  const background = _.get(props, 'properties.desktop.background');

  const editMode = useSelector(state => {
    return _.get(state, `${editorSession}.present.editMode`) === id
  });

  const changeView = (view) => {
    dispatch(toggleEditMode(editorSession, props.uid, false));
    setView(view);
  }

  const closePopup = () => {
    dispatch(toggleEditMode(editorSession, props.uid, false));
    setView(null);
  }

  return (
    <>
      <Property.Action className={styles.textButton} onClick={() => changeView('background')}>
        Change Background
      </Property.Action>
      <Property.Divider />
      <Property.Action className={styles.iconButton} onClick={() => changeView('style')}>
        <i className={cx("cleverstory-customize")} />
      </Property.Action>
      <Property.Divider />
      {/* <Property.Action className={styles.iconButton} onClick={() => changeView('scroll')}>
        <i className={cx("cleverstory-scroll")} />
      </Property.Action> */}
      <Property.Divider />
      <Property.Action className={styles.iconButton} onClick={() => { window.open(`${process.env.REACT_APP_CLEVERSTORY_SUPPORT}`, '_blank') }}>
        <i className={'cleverstory-help'} />
      </Property.Action>

      {!editMode && view === 'background' &&
        <Background.Dialog id={id}
          value={background}
          onChange={(value) => {
            dispatch(updateWidgetProperty(editorSession, id, 'desktop', {
              background: value
            }));
          }}
          onCancel={() => {
            closePopup();
          }}
        />
      }

      {!editMode && view === 'style' &&
        <FilterStyleEditor
          id={id}
          editor={editorSession}
          widget={props}
          onCancel={() => {
            closePopup()
          }}
        />
      }

      {!editMode && view === 'liveElements' &&
        <LiveElementCreationProperty elements={[props]} onCancel={() => closePopup()} />
      }

      {!editMode && view === 'scroll' &&
        <ScrollEffectDialog
          id={id}
          editor={editorSession}
          widget={props}
          onCancel={() => {
            closePopup()
          }}
        />
      }
    </>
  );
}

const FilterStyleEditor = ({ id, widget, position, ...rest }) => {
  const dispatch = useDispatch();

  const editorContext = React.useContext(EditorContext);
  const editorSession = editorContext.editorSession.id;
  const breakpoint = useSelector(state => _.get(state, `${editorSession}.present.breakpoint`));
  const properties = _.get(widget, `properties.${breakpoint}`, {});
  const parentWidgetProps = useWidgetSelector(editorSession, _.get(widget, 'parent'));
  const filterSectionHeight = parseInt(_.get(properties, 'layout.height'));
  const widgets = useSelector(state => _.get(state, `${editorSession}.present.widgets`) || []);
  const [maxHorizontalMargin, setMaxHorizontalMargin] = useState(0);

  const showFilter = _.get(parentWidgetProps, `properties.desktop.filters`, false);
  const showSearch = _.get(parentWidgetProps, `properties.desktop.search`, false);

  const maxVerticalMargin = useSelector(state => {
    const widgets = _.get(state, `${editorSession}.present.widgets`) || [];
    let filterContainer = widgets.find(item => item.parent === id && item.type === CONTENT_FILTER_CONTAINER);
    let searchContainer = widgets.find(item => item.parent === id && item.type === CONTENT_SEARCH);
    const filterElement = filterContainer ? document.getElementById(filterContainer.uid) : null;
    const searchElement = searchContainer ? document.getElementById(searchContainer.uid) : null;

    if (filterElement && searchElement) {
      const filterHeight = _.get(filterElement, 'clientHeight', 0) || 0;
      const searchHeight = _.get(searchElement, 'clientHeight', 0) || 0;
      return filterSectionHeight - parseInt(filterHeight + searchHeight);
    }
    return filterSectionHeight
  });

  const calculateMaxHorizontalMargin = () => {
    const parentContainer = widgets.find(item => item.uid === id && item.type === CONTENT_FILTER);
    const filterContainer = widgets.find(item => item.parent === id && item.type === CONTENT_FILTER_CONTAINER);
    const searchContainer = widgets.find(item => item.parent === id && item.type === CONTENT_SEARCH);
  
    const filterElement = filterContainer ? document.getElementById(filterContainer.uid) : null;
    const searchElement = searchContainer ? document.getElementById(searchContainer.uid) : null;
    const parentContainerElement = parentContainer ? document.getElementById(parentContainer.uid) : null;
    const filterSectionWidth = parentContainerElement?.clientWidth || 0;
  
    if (filterElement || searchElement) {
      const searchWidth = _.get(searchElement, 'clientWidth') || 0;
      if(breakpoint === 'mobile') { 
        setMaxHorizontalMargin(filterSectionWidth - searchWidth);
      } else {
        setMaxHorizontalMargin(filterSectionWidth - _.max([375, searchWidth]));
      }
    } else {
      setMaxHorizontalMargin(filterSectionWidth);
    }
  };

  useEffect(() => {
      calculateMaxHorizontalMargin();
  }, [breakpoint]);

  const alignment = _.get(properties, 'alignment', {
    position: 'center',
    margin: breakpoint === 'desktop' ? 5 : 15
  });

  const spacing = _.get(properties, 'spacing', 16);
  const horizontalColumn = _.get(properties, 'horizontalColumn', false);
  const columnReverse = _.get(properties, 'columnReverse', false);


  const handlePropertyChange = (value) => {
    dispatch(updateWidgetProperty(editorSession, id, breakpoint, value));
  }

  const handleLayoutChange = (type, value) => {
    let layoutRequest = {};

    if (type === 'horizontalColumn') {
      layoutRequest = {
        ...properties,
        horizontalColumn: value
      }
    }

    if (type === 'columnReverse') {
      layoutRequest = {
        ...properties,
        columnReverse: value
      }
    }

    handlePropertyChange(layoutRequest);
  }

  const handleAlignmentChange = (value) => {
    const position = _.get(value, 'position');


    if (position === 'center') {
      value = {
        ...value,
        margin: 5
      }
    }

    var request = {
      ...properties,
      alignment: {
        ...alignment,
        ...value
      }
    }

    handlePropertyChange(request);
  }

  const handleSpacing = (value) => {
    if (!value) {
      return;
    }

    var spacingRequest = {
      ...properties,
      spacing: value
    }

    handlePropertyChange(spacingRequest);
  }

  return (
    <Property.Dialog className={styles.fontDialog}
      width={246}
      target={`#${id}`}
      title={'Layout Setting'}
      onCancel={rest.onCancel}
    >
      <Property.Body>
        <Property nameStyle={styles.propertyName} className={styles.buttonAlignmentProperty}>
          {(showFilter && showSearch && breakpoint == 'desktop') &&
            <ArrangeLayout
              horizontalColum={horizontalColumn}
              columnReverse={columnReverse}
              handleLayoutChange={handleLayoutChange}
            />
          }
          {!horizontalColumn &&
            <>
              <FilterContainerHorizontalAlignment
                value={alignment}
                showFilter={showFilter}
                showSearch={showSearch}
                breakpoint={breakpoint}
                columnReverse={columnReverse}
                handleLayoutChange={handleLayoutChange}
                maxHorizontalMargin={maxHorizontalMargin}
                onChange={handleAlignmentChange}
              />
              {(showFilter && showSearch) &&
                <div className={styles.inputWrapper}>
                  <span className={styles.name}>Space Between Items</span>
                  <Property.Slider
                    input
                    min={0}
                    max={maxVerticalMargin}
                    value={spacing}
                    onChange={handleSpacing}
                  />
                </div>
              }
            </>
          }
        </Property>
      </Property.Body>
    </Property.Dialog>
  );
}

const FilterWidgetMobileAction = (props) => {
  const dispatch = useDispatch();
  const [view, setView] = useState(null);

  const editorContext = React.useContext(EditorContext);
  const editorSession = editorContext.editorSession.id;
  // const isGroupContainer = useGroupContainerSelector(editorSession, props);
  // const template = _.get(props, 'template.widget') != null;
  const id = _.get(props, 'uid');

  const editMode = useSelector(state => {
    return _.get(state, `${editorSession}.present.editMode`) === id
  });
  const horizontalColumn = _.get(props, 'properties.mobile.horizontalColumn', false);

  const changeView = (view) => {
    dispatch(toggleEditMode(editorSession, props.uid, false));
    setView(view);
  }

  const closePopup = () => {
    dispatch(toggleEditMode(editorSession, props.uid, false));
    setView(null);
  }

  return (
    <>
      {!horizontalColumn &&
        <Property.Action className={styles.iconButton} onClick={() => changeView('style')}>
          <i className={cx("cleverstory-customize")} />
        </Property.Action>
      }

      {!editMode && view === 'style' &&
        <FilterStyleEditor
          id={id}
          editor={editorSession}
          widget={props}
          onCancel={() => {
            closePopup()
          }}
        />
      }
    </>
  );
}

const ArrangeLayout = ({ horizontalColum, columnReverse, handleLayoutChange }) => {

  return (
    <div className={styles.filterArrangement}>
      <span className={styles.subDialogName}>Arrangement</span>
      <div className={styles.optionWrapper}>
        <div className={styles.options}>
          <button onClick={() => { handleLayoutChange('horizontalColumn', false) }} className={cx(styles.alignment,
            {
              [styles.active]: !horizontalColum
            })}>
            <i className={cx('cleverstory-stack-vertically')}></i>
          </button>
          <button onClick={() => { handleLayoutChange('horizontalColumn', true) }} className={cx(styles.alignment,
            {
              [styles.active]: horizontalColum
            })}>
            <i className={cx('cleverstory-stack-horizontally')}></i>
          </button>
        </div>
        <button onClick={() => { handleLayoutChange('columnReverse', !columnReverse) }} className={cx(styles.reset,
          {
            [styles.active]: columnReverse
          }
        )}>
          <i className={cx('cleverstory-sync')}></i>
        </button>
      </div>
    </div>
  )
}

const FilterContainerHorizontalAlignment = ({ value, maxHorizontalMargin, columnReverse, handleLayoutChange, breakpoint, showFilter, showSearch, onChange }) => {

  const position = _.get(value, 'position') || 'center';
  const margin = _.get(value, 'margin', 0);


  const handleAlignmentChange = (position) => {
    var request = {
      position: position
    }

    if (position === 'right' || position === 'left') {
      request = {
        ...request,
        margin: breakpoint === 'desktop' ? 5 : 15
      }
    }

    onChange(request);
  }

  const handleMarginChange = (margin) => {
    var request = {
      margin: margin
    }

    onChange(request);
  }

  return (
    <div className={styles.buttonAlignment}>
      <span className={styles.subDialogName}>Alignment</span>
      <div className={styles.wrapperOption}>
        <div className={styles.options}>
          <button className={cx(styles.alignment,
            {
              [styles.active]: position === 'left'
            })}
            onClick={handleAlignmentChange.bind(this, 'left')}
          >
            <i className={cx('cleverstory-widgets-align-left')}></i>
          </button>
          <button className={cx(styles.alignment,
            {
              [styles.active]: position === 'center'
            })}
            onClick={handleAlignmentChange.bind(this, 'center')}
          >
            <i className={cx('cleverstory-widgets-align-center')}></i>
          </button>
          <button className={cx(styles.alignment,
            {
              [styles.active]: position === 'right'
            })}
            onClick={handleAlignmentChange.bind(this, 'right')}
          >
            <i className={cx('cleverstory-widgets-align-right')}></i>
          </button>
        </div>
        {(breakpoint == 'mobile' && showFilter && showSearch) &&
          <button onClick={() => { handleLayoutChange('columnReverse', !columnReverse) }} className={cx(styles.reset,
            {
              [styles.active]: columnReverse
            }
          )}>
            <i className={cx('cleverstory-sync')}></i>
          </button>}
      </div>

      {position !== 'center' &&
        <Property name="Margin" className={styles.marginProperty}>
          <Property.Slider
            input
            min={breakpoint === 'desktop' ? 5 : 0}
            max={maxHorizontalMargin > 0 ? maxHorizontalMargin : 30}
            step={1}
            value={margin}
            onChange={handleMarginChange}
          />
        </Property>
      }
    </div>
  )
}

const ScrollEffectDialog = ({ id, editor, widget, onCancel }) => {
  const dispatch = useDispatch();

  const scrollEffect = _.get(widget, `properties.desktop.scrollEffect`, 'NONE');

  const filterSectionEffects = SCROLL_EFFECTS.filter(effect => effect.section === 'menu');
  const backgroundEffects = SCROLL_EFFECTS.filter(effect => effect.section === 'background');

  const handlePropertyChange = (value) => {
    var request = {
      scrollEffect: value
    }
    dispatch(updateWidgetProperty(editor, id, 'desktop', request));
  }

  return (
    <Property.Dialog className={styles.scrollEffectDialog}
      width={279}
      target={`#${id}`}
      title={'Scroll Effects'}
      onCancel={onCancel}
    >
      <Property.Body>
        <Property className={styles.scrollEffectProperty}>
          <span className={styles.subDialogName}>Entire Filter Row</span>
          <div className={styles.optionContainer}>
            {filterSectionEffects.slice(0, 2).map((effects, index) => {
              return (
                <ScrollAnimationCard
                  effects={effects}
                  index={index}
                  handlePropertyChange={handlePropertyChange}
                  scrollEffect={scrollEffect}
                />
              )
            })
            }
          </div>
        </Property>
      </Property.Body>
    </Property.Dialog>
  )
}

const ScrollAnimationCard = ({ index, effects, scrollEffect, handlePropertyChange }) => {

  const [stopped, setStopped] = useState(true)

  const options = {
    loop: false,
    autoplay: false,
    animationData: require(`assets/images/scrollEffects/${effects.type.toLowerCase()}.json`),
    rendererSettings: {
      preserveAspectRatio: "xMidYMid slice"
    }
  };

  return (
    <div
      key={index}
      onMouseEnter={() => { setStopped(false) }}
      onMouseLeave={() => { setStopped(true) }}
      onClick={() => { handlePropertyChange(effects.type) }}
      className={cx(styles.scrollEffectCard)}>
      <div className={cx(styles.effectOverview, { [styles.active]: scrollEffect === effects.type })}>
        <Lottie options={options} isClickToPauseDisabled isStopped={stopped} />
      </div>
      <span className={styles.effectsTitle}>{effects.name}</span>
    </div>
  )
}


function getStyles(widgetProps, breakpoint) {
  const margin = _.get(widgetProps, 'properties.desktop.margin', 0);
  const padding = _.get(widgetProps, `properties.${breakpoint}.padding`, 0);

  var widgetStyles = {
    marginLeft: 0,
    paddingTop: `${padding}px`,
    paddingBottom: `${padding}px`
  }

  if (breakpoint === 'desktop') {
    widgetStyles = {
      ...widgetStyles,
      paddingLeft: `${padding}px`,
      width: '100%',
      paddingRight: `${padding}px`
    }
  }

  if (breakpoint === 'mobile') {
    widgetStyles = {
      ...widgetStyles,
    }
  }

  return widgetStyles;
}

function getContainerAlignment(widgetProps, breakpoint, filters) {
  const alignment = _.get(widgetProps, `properties.${breakpoint}.alignment`, {
    position: 'center',
    margin: 0
  });
  const horizontalColumn = _.get(widgetProps, `properties.${breakpoint}.horizontalColumn`, false);
  const columnReverse = _.get(widgetProps, `properties.${breakpoint}.columnReverse`, false);
  const spacing = _.get(widgetProps, `properties.${breakpoint}.spacing`, 16);


  const verticalAlignment = _.get(widgetProps, `properties.${breakpoint}.verticalAlignment`, {
    position: 'center',
    margin: 0
  });

  var position = _.get(alignment, 'position', 'center');
  var margin = _.get(alignment, 'margin', 0);

  var verticalPosition = _.get(verticalAlignment, 'position', 'center');
  var verticalMargin = _.get(verticalAlignment, 'margin', 0);

  var containerStyles = {}

  //verticalAlignment
  containerStyles['alignItems'] = position;

  if (position === 'left') {
    containerStyles = {
      ...containerStyles,
      alignItems: 'flex-start',
      paddingLeft: breakpoint == 'mobile' ? '0px' : `${margin}px`
    }
  } else if (position === 'right') {
    containerStyles = {
      ...containerStyles,
      alignItems: 'flex-end',
      paddingRight: breakpoint == 'mobile' ? '0px' : `${margin}px`
    }
  }

  //alignment
  containerStyles['justifyContent'] = verticalPosition;

  if (verticalPosition === 'top') {
    containerStyles = {
      ...containerStyles,
      justifyContent: 'flex-start',
      paddingTop: `${verticalMargin}px`
    }
  }
  else if (verticalPosition === 'bottom') {
    containerStyles = {
      ...containerStyles,
      justifyContent: 'flex-end',
      paddingBottom: `${verticalMargin}px`
    }
  }

  //spacing between items
  if (spacing && !horizontalColumn) {
    containerStyles = {
      ...containerStyles,
      rowGap: `${filters ? spacing : 2}px`
    }
  }

  if (breakpoint == 'mobile') {
    containerStyles = {
      ...containerStyles,
      flexDirection: columnReverse ? 'column-reverse' : 'column',
    }
    return containerStyles;
  }

  //horizontalLayout
  containerStyles['flexDirection'] = 'column';
  if (horizontalColumn && breakpoint == 'desktop') {
    containerStyles = {
      ...containerStyles,
      flexDirection: 'row',
      justifyContent: 'space-between',
      paddingLeft: '0px',
      paddingRight: '0px'
    }
  }

  //reverse direction
  if (columnReverse) {
    containerStyles = {
      ...containerStyles,
      flexDirection: horizontalColumn ? 'row-reverse' : 'column-reverse'
    }
  }

  return containerStyles;
}

function getDropDownAlignment(widgetProps, breakpoint) {
  const alignment = _.get(widgetProps, `properties.${breakpoint}.alignment`, {
    position: 'center',
    margin: 0
  });

  const verticalAlignment = _.get(widgetProps, `properties.${breakpoint}.verticalAlignment`, {
    position: 'center',
    margin: 0
  });

  var position = _.get(alignment, 'position', 'center');
  var margin = _.get(alignment, 'margin', 0);

  var verticalPosition = _.get(verticalAlignment, 'position', 'center');
  var verticalMargin = _.get(verticalAlignment, 'margin', 0);

  let containerStyles = {}

  //alignment
  containerStyles['justifyContent'] = position;

  if (position === 'left') {
    containerStyles = {
      ...containerStyles,
      justifyContent: 'flex-start',
      paddingLeft: `${margin}px`
    }
  } else if (position === 'right') {
    containerStyles = {
      ...containerStyles,
      justifyContent: 'flex-end',
      paddingRight: `${margin}px`
    }
  }

  //verticalAlignment
  containerStyles['alignItems'] = verticalPosition;

  if (verticalPosition === 'top') {
    containerStyles = {
      ...containerStyles,
      alignItems: 'flex-start',
      paddingTop: `${verticalMargin}px`
    }
  }
  else if (verticalPosition === 'bottom') {
    containerStyles = {
      ...containerStyles,
      alignItems: 'flex-end',
      paddingBottom: `${verticalMargin}px`
    }
  }

  return containerStyles;
}

FilterWidget.sizeConfig = async (widget, mediaCache) => {
  const url = _.get(widget, 'properties.desktop.background.media.url', '');
  if (!url) {
    return {
      size: 0,
      type: 'image'
    };
  }
  return {
    size:  await getMediaInfo(`${IMAGE_CDN_ENDPOINT}/${url}`, mediaCache) || 0, // MB
    type: 'image'
  };
}

FilterWidget.actions = (editorSession, breakpoint, props) => {
  return (
    <>
      {breakpoint === 'desktop' &&
        <FilterWidgetDesktopAction editorSession={editorSession} breakpoint={breakpoint} {...props} />
      }
      {breakpoint === 'mobile' &&
        <FilterWidgetMobileAction editorSession={editorSession} breakpoint={breakpoint} {...props} />
      }
    </>
  );
}

FilterWidget.processRequest = (widget) => {
  var background = _.get(widget, 'properties.desktop.background');
  var mobileBackground = _.get(widget, 'properties.mobile.background');

  if (!background) {
    return widget;
  }

  const mediaId = _.get(background, 'media.id');
  if (!mediaId) {
    return widget;
  }

  return {
    ...widget,
    properties: {
      ...widget.properties,
      desktop: {
        ...widget.properties.desktop,
        background: {
          ...background,
          media: mediaId
        }
      },
      mobile: {
        ...widget.properties.mobile,
        background: {
          ...mobileBackground,
          media: mediaId
        }
      }
    }
  }
}

FilterWidget.moveableConfig = (editorSession, widget, breakpoint) => {
  const desktop = breakpoint === 'desktop';

  return {
    draggable: false,
    resizable: true,
    rotatable: false,
    selectable: true,
    cloneable: false,
    deletable: false,
    renderDirections: ['n', 's'],
    onDrag: (layout) => {
      const frame = new Frame(layout);
      const translate = frame.get("transform", "translate");
      const values = _.split(translate, ",");

      var y = parseInt(values[1]);
      y = Math.max(0, y);

      const newTranslate = `0px, ${y}px`;

      frame.set("transform", "translate", newTranslate);
      return frame.get();
    },
    onResize: (widget, layout, breakpoint) => {
      const frame = new Frame(layout);
      frame.set('width', '100%');
      return frame.get();
    },
    onReparent: (widget, parent) => {
      return false
    }
  }
}


export { FilterWidget };